<template>
  <span
    v-if="source"
    class="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded"
    :class="badgeClass"
    data-test="source-badge"
  >
    <FontAwesomeIcon :icon="icon" class="text-[9px]" />
    {{ label }}
  </span>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

/** @typedef {import('@/services/api/schema').components['schemas']['RequirementSourceEnum']} RequirementSource */
/** @typedef {{ label: string, icon: [string, string], class: string }} BadgeConfig */

/**
 * Ticket sources (RequirementSourceEnum, from the backend schema) that get their
 * own look. Any other value still renders, labelled with its own name.
 * @type {Partial<Record<RequirementSource, BadgeConfig>>}
 */
const TICKET_SOURCE_CONFIG = {
  email: {
    label: 'Email',
    icon: ['fas', 'envelope'],
    class: 'bg-blue-50 text-blue-700',
  },
  web: {
    label: 'Web',
    icon: ['fas', 'globe'],
    class: 'bg-gray-100 text-gray-600',
  },
  pylon: {
    label: 'Pylon',
    icon: ['fas', 'headset'],
    class: 'bg-violet-50 text-violet-700',
  },
  cerberus: {
    label: 'Cerberus',
    icon: ['fas', 'robot'],
    class: 'bg-emerald-50 text-emerald-700',
  },
};

/** Comment-only source (RequirementComment.source). */
const COMMENT_SOURCE_CONFIG = {
  internal_note: {
    label: 'Internal Note',
    icon: ['fas', 'lock'],
    class: 'bg-amber-50 text-amber-700',
  },
};

/** @param {string} source @returns {BadgeConfig} */
function fallbackConfig(source) {
  const label = source
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return { label, icon: ['fas', 'tag'], class: 'bg-gray-100 text-gray-600' };
}

export default {
  name: 'SourceBadge',
  components: { FontAwesomeIcon },
  props: {
    source: { type: String, default: null },
  },
  computed: {
    /** @returns {BadgeConfig} */
    config() {
      return (
        TICKET_SOURCE_CONFIG[this.source] ||
        COMMENT_SOURCE_CONFIG[this.source] ||
        fallbackConfig(this.source)
      );
    },
    label() {
      return this.config.label;
    },
    icon() {
      return this.config.icon;
    },
    badgeClass() {
      return this.config.class;
    },
  },
};
</script>
